import { Injectable, TemplateRef } from '@angular/core';
import { WindowCloseResult, WindowService, WindowRef, WindowComponent, DialogService, DialogRef, DialogSettings, DialogAction, DialogResult } from '@progress/kendo-angular-dialog';
import { WindowSettings } from '@progress/kendo-angular-dialog';
import { EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import { I18nService } from '../i18n/i18n.service';
import { HtmlContentDialogComponent } from './html-content-dialog.component';
import { focusNearestElement } from 'app/shared/utils/focusHelper';
import * as _ from 'lodash';

export enum ConfirmDeleteResult {
  Yes = 'yes',
  No = 'no'
}

/**
 * cancel method: will be called on WindowClose (click close button on windowFrame)
 */
export interface ICanCancel {
  cancel: (event?: any) => void;
}

/**
 * preventClose method: will be called before WindowClose to determine if closing should be prevented
 */
export interface ICanPreventClose {
  preventClose: (event?: any) => boolean;
}

/**
 * event emitter is triggered on WindowClose event: can be used to detect type of close (cancel, save ..)
 */
export interface ITriggerOnClose {
  closeWindow: EventEmitter<any>;
}

/**
 * TypeGuard for ICanCancel Interface: DuckTyping
 * @param test
 */
export function isICanCancel(test: any): test is ICanCancel {
  return test.cancel !== undefined;
}

/**
 * TypeGuard for ICanPreventClose Interface: DuckTyping
 * @param test
 */
export function isICanPreventClose(test: any): test is ICanPreventClose {
  return test.preventClose !== undefined;
}

/**
 * TypeGuard for ITriggerOnClose Interface: DuckTyping
 * @param test
 */
export function isITriggerOnClose(test: any): test is ITriggerOnClose {
  return test.closeWindow !== undefined;
}

/**
 * windows settings.
 */
export class CustomWindowSettings {
  public type?: string = 'default';
  /**
   * Sets the title of the Window.
   */
  public title?: string;
  /**
   * Defines the content of the Window.
   */
  public content?: string | TemplateRef<any> | Function;
  /**
   * Specifies the width of the Window.
   */
  public width?: number;  
  /**
   * Optional trigger element to restore focus to after window closes
   */
  public triggerElement?: HTMLElement;
}

/**
 * dialog settings 
 */
export class CustomDialogSettings {
  /**
   * Sets the title of the Window.
   */
  public title?: string;  
  /**
   * Defines the content of the Dialog.
   */
  public content?: string | TemplateRef<any> | Function;  
  /**
   * Sets the action buttons of the Dialog.
   */
  public actions?: DialogAction[] | any[] | TemplateRef<any>;  
  /**
   * Specifies the width of the Window.
   */
  public width?: number;    
  /**
   * Optional trigger element to restore focus to after dialog closes
   */
  public triggerElement?: HTMLElement;
  /**
   * Sets the focused element query selector.
   */
  public autoFocusedElement?: string;  
  /**
   * Defines a predicate that verifies if the pressed dialog action should be prevented. Returning true from the predicate prevents the dialog from closing.
   * If the **Close** button of the title bar is clicked, `DialogResult` is a `DialogCloseResult` instance.
   * If the Dialog is closed through the action buttons, `DialogResult` contains the object that was passed when the Dialog was opened. ([see example]({% slug service_dialog %}#toc-closing-the-dialog))
   * @param {DialogResult} ev
   * @param {DialogRef} [dialogRef] - provided only when the dialog is created using a component.
   * @returns
   */
  public preventAction?: (ev: DialogResult, dialogRef?: DialogRef) => boolean;
  /**
   * When true, removes the dialog's inner padding so the contained component fills the full space.
   */
  public flushContent?: boolean;
}

@Injectable()
export class CustomWindowService {
  private static readonly VIEWPORT_HEIGHT_THRESHOLD = 500;
  private static readonly DEFAULT_WINDOW_WIDTH = 600;
  private static readonly DEFAULT_DIALOG_WIDTH = 400;
  private static readonly ITEM_NAME_MAX_LENGTH = 50;
  private static readonly ITEM_NAME_TRUNCATE_LENGTH = 47;
  private static readonly NARROW_WINDOW_THRESHOLD = 600;
  private static readonly NARROW_WINDOW_CLASS = 'window--narrow';
  private static readonly FLUSH_CONTENT_DIALOG_CLASS = 'dialog--flush-content';

  private windows: Map<string, WindowRef> = new Map();

  constructor(
    private windowService: WindowService,
    private dialogService: DialogService,
    private i18n: I18nService
  ) {}

  // ========================================
  // Public Window Methods
  // ========================================

  /**
   * Opens a window and returns the content component instance
   * @param params Window configuration settings
   * @returns The component instance rendered in the window
   */
  public open(params: CustomWindowSettings) {
    const windowRef = this.openWindow(params);
    return windowRef.content.instance;
  }

  /**
   * Opens a window and returns the WindowRef for full control
   * @param params Window configuration settings
   * @returns WindowRef for subscribing to window events and controlling the window
   */
  public openWindow(params: CustomWindowSettings): WindowRef {
    const triggerElement = params.triggerElement || (document.activeElement as HTMLElement);

    const windowSettings = this.createWindowSettings(params);
    const existingWindow = this.getWindow(params.type);
    if (existingWindow) {
      existingWindow.close();
    }

    const windowRef = this.windowService.open(windowSettings);
    this.setWindow(params.type, windowRef);

    this.configureWindow(windowRef, params.type, triggerElement);
    return windowRef;
  }

  /**
   * Gets a window reference by type
   * @param type Window type identifier (default: 'default')
   * @returns WindowRef if exists, undefined otherwise
   */
  public getWindow(type: string = 'default'): WindowRef | undefined {
    return this.windows.get(type);
  }

  /**
   * Closes a window by type
   * @param type Window type identifier (default: 'default')
   */
  public close(type: string = 'default'): void {
    const windowRef = this.getWindow(type);
    if (windowRef) {
      windowRef.close();
    }
  }

  // ========================================
  // Public Dialog Methods
  // ========================================

  /**
   * Opens a dialog with automatic focus restoration
   * @param params Dialog settings with optional triggerElement
   * @returns DialogRef for subscribing to results
   */
  public openDialog(params: CustomDialogSettings): DialogRef {
    const triggerElement = params.triggerElement || (document.activeElement as HTMLElement);

    const dialogSettings = this.createDialogSettings(params);
    const dialogRef = this.dialogService.open(dialogSettings);

    if (params.flushContent) {
      const dialogElement = dialogRef.dialog.location.nativeElement as HTMLElement;
      dialogElement.classList.add(CustomWindowService.FLUSH_CONTENT_DIALOG_CLASS);
    }

    const subscription = dialogRef.result.subscribe(() => {
      focusNearestElement(triggerElement);
      subscription.unsubscribe();
    });

    return dialogRef;
  }

  /**
   * Opens a confirmation dialog for delete operations
   * @param itemName The name of the item to be deleted (will be highlighted in the message)
   * @param triggerElement Optional trigger element for focus restoration
   * @returns Observable that emits ConfirmDeleteResult.Yes or ConfirmDeleteResult.No
   */
  public confirmDelete(itemName: string, triggerElement?: HTMLElement): Observable<ConfirmDeleteResult> {
    const message = this.i18n.getLocalizedString('dialog.confirmDelete.message');
    const title = this.i18n.getLocalizedString('dialog.confirmDelete.title');
    const yesLabel = this.i18n.getLocalizedString('dialog.confirmDelete.yes');
    const noLabel = this.i18n.getLocalizedString('dialog.confirmDelete.no');

    const truncatedItemName = this.truncateItemName(itemName);
    const htmlContent = message.replace('{{item}}', `<strong title="${this.escapeHtml(itemName)}">${this.escapeHtml(truncatedItemName)}</strong>`);

    const dialogRef: DialogRef = this.openDialog({
      title: title,
      content: HtmlContentDialogComponent,
      actions: [
        { text: yesLabel, primary: true },
        { text: noLabel }
      ],
      triggerElement: triggerElement
    });

    const componentInstance = dialogRef.content.instance as HtmlContentDialogComponent;
    componentInstance.html = htmlContent;

    return new Observable<ConfirmDeleteResult>(observer => {
      const subscription = dialogRef.result.subscribe((result: any) => {
        const userConfirmed = result && result.text === yesLabel;
        observer.next(userConfirmed ? ConfirmDeleteResult.Yes : ConfirmDeleteResult.No);
        observer.complete();
        subscription.unsubscribe();
      });
    });
  }

  // ========================================
  // Private Helper Methods
  // ========================================

  /**
   * Creates window settings with defaults
   */
  private createWindowSettings(params: CustomWindowSettings): WindowSettings {
    const windowSettings = new WindowSettings();
    windowSettings.title = params.title;
    windowSettings.content = params.content;
    windowSettings.width = params.width ?? CustomWindowService.DEFAULT_WINDOW_WIDTH;

    const clientHeight = document.documentElement.clientHeight;
    windowSettings.top = clientHeight > CustomWindowService.VIEWPORT_HEIGHT_THRESHOLD
      ? (clientHeight - CustomWindowService.VIEWPORT_HEIGHT_THRESHOLD) / 10
      : 0;


    if (windowSettings.preventClose === undefined) {
      windowSettings.preventClose = (_, windowRef) => {
        const componentInstance = windowRef.content.instance;
        return isICanPreventClose(componentInstance) ? componentInstance.preventClose() : false;
      };
    }

    return windowSettings;
  }

  /**
   * Creates dialog settings with defaults
   */
  private createDialogSettings(params: CustomDialogSettings): DialogSettings {
    const dialogSettings: DialogSettings = {
      title: params.title,
      content: params.content,
      actions: params.actions,
      width: params.width ?? CustomWindowService.DEFAULT_DIALOG_WIDTH,
      autoFocusedElement: params.autoFocusedElement
    };

    if (params.preventAction !== undefined) {
      dialogSettings.preventAction = params.preventAction;
    } else {
      dialogSettings.preventAction = (ev, dialogRef) => {
        if (dialogRef) {
          const componentInstance = dialogRef.content.instance;
          return isICanPreventClose(componentInstance) ? componentInstance.preventClose() : false;
        }
        return false;
      };
    }

    return dialogSettings;
  }

  /**
   * Configures window behavior, event handlers, and lifecycle
   */
  private configureWindow(windowRef: WindowRef, windowType: string, triggerElement: HTMLElement): void {
    const contentComponent = windowRef.content.instance;
    const windowComponent = windowRef.window.instance;
    const windowElement = this.getWindowElement(windowComponent);

    this.setInitialMaxHeight(windowComponent, windowElement);
    setTimeout(() => this.updateNarrowClass(windowElement));
    this.setupEventHandlers(windowComponent, windowElement);
    this.setupWindowLifecycle(windowRef, contentComponent, windowType, triggerElement);
  }

  /**
   * Sets initial max-height to prevent window from exceeding viewport
   */
  private setInitialMaxHeight(windowComponent: WindowComponent, windowElement: HTMLElement): void {
    if (windowComponent.top > 0) {
      const clientHeight = document.documentElement.clientHeight;
      const maxHeight = clientHeight - windowComponent.top;
      windowElement.style.maxHeight = `${maxHeight}px`;
    }
  }

  /**
   * Sets up event handlers for window resize, drag, and manual resize
   */
  private setupEventHandlers(windowComponent: WindowComponent, windowElement: HTMLElement): void {
    windowComponent.resizeDirections = ['s', 'e'];

    const viewportResizeHandler = () => this.constrainToViewport(windowComponent, windowElement);
    window.addEventListener('resize', viewportResizeHandler);

    const dragEndSubscription = windowComponent.dragEnd.subscribe(() =>
      this.constrainToViewport(windowComponent, windowElement)
    );

    const resizeEndSubscription = windowComponent.resizeEnd.subscribe(() =>
      this.applyActualDimensions(windowComponent, windowElement)
    );

    // Store cleanup handlers on the window component for later removal
    (windowComponent as any)._cleanupHandlers = {
      viewportResizeHandler,
      dragEndSubscription,
      resizeEndSubscription
    };
  }

  /**
   * Sets up window lifecycle (close event, focus restoration)
   */
  private setupWindowLifecycle(
    windowRef: WindowRef,
    contentComponent: any,
    windowType: string,
    triggerElement: HTMLElement
  ): void {
    let wasClosedByComponent = false;

    if (isITriggerOnClose(contentComponent)) {
      contentComponent.closeWindow.subscribe(() => {
        wasClosedByComponent = true;
        windowRef.close();
      });
    }

    windowRef.result.subscribe(result => {
      this.cleanupEventHandlers(windowRef.window.instance);

      const wasClosedViaCloseButton = result instanceof WindowCloseResult && !wasClosedByComponent;
      if (wasClosedViaCloseButton && isICanCancel(contentComponent)) {
        contentComponent.cancel();
      }

      this.setWindow(windowType, null);
      focusNearestElement(triggerElement);
    });
  }

  /**
   * Cleans up event handlers and subscriptions
   */
  private cleanupEventHandlers(windowComponent: WindowComponent): void {
    const cleanupHandlers = (windowComponent as any)._cleanupHandlers;
    if (cleanupHandlers) {
      window.removeEventListener('resize', cleanupHandlers.viewportResizeHandler);
      cleanupHandlers.dragEndSubscription.unsubscribe();
      cleanupHandlers.resizeEndSubscription.unsubscribe();
      delete (windowComponent as any)._cleanupHandlers;
    }
  }

  /**
   * Stores or removes a window reference
   */
  private setWindow(type: string = 'default', value: WindowRef | null): void {
    if (value === null) {
      this.windows.delete(type);
    } else {
      this.windows.set(type, value);
    }
  }

  /**
   * Gets the native window DOM element
   */
  private getWindowElement(windowComponent: WindowComponent): HTMLElement {
    return windowComponent['el'].nativeElement as HTMLElement;
  }

  /**
   * Ensures window stays within viewport boundaries
   */
  private constrainToViewport(windowComponent: WindowComponent, windowElement: HTMLElement): void {
    const viewportHeight = document.documentElement.clientHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const windowHeight = windowElement.offsetHeight;
    const windowWidth = windowElement.offsetWidth;

    // Constrain vertical position
    const windowBottom = windowComponent.top + windowHeight;
    if (windowBottom > viewportHeight) {
      windowComponent.setOffset('top', Math.max(0, viewportHeight - windowHeight));
    } else if (windowComponent.top < 0) {
      windowComponent.setOffset('top', 0);
    }

    // Constrain horizontal position
    const windowRight = windowComponent.left + windowWidth;
    if (windowRight > viewportWidth) {
      windowComponent.setOffset('left', Math.max(0, viewportWidth - windowWidth));
    } else if (windowComponent.left < 0) {
      windowComponent.setOffset('left', 0);
    }

    // Lock height to actual size and remove max-height constraint
    windowComponent.setDimension('height', windowHeight);
    windowElement.style.removeProperty('max-height');
    this.updateNarrowClass(windowElement);
  }

  /**
   * Applies actual rendered dimensions to prevent CSS max-width/max-height issues
   */
  private applyActualDimensions(windowComponent: WindowComponent, windowElement: HTMLElement): void {
    windowComponent.setDimension('width', windowElement.offsetWidth);
    windowComponent.setDimension('height', windowElement.offsetHeight);
    windowElement.style.removeProperty('max-height');
    this.updateNarrowClass(windowElement);
  }

  /**
   * Adds or removes the narrow-window class based on whether the window width
   * is below NARROW_WINDOW_THRESHOLD, allowing forms to apply a responsive stacked layout.
   */
  private updateNarrowClass(windowElement: HTMLElement): void {
    const isNarrow = windowElement.offsetWidth < CustomWindowService.NARROW_WINDOW_THRESHOLD;
    windowElement.classList.toggle(CustomWindowService.NARROW_WINDOW_CLASS, isNarrow);
  }

  /**
   * Truncates item name for display if longer than max length
   */
  private truncateItemName(itemName: string): string {
    return itemName.length <= CustomWindowService.ITEM_NAME_MAX_LENGTH
      ? itemName
      : itemName.substring(0, CustomWindowService.ITEM_NAME_TRUNCATE_LENGTH) + '...';
  }

  /**
   * Escapes HTML special characters to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
